import React, { useRef, useState } from 'react';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { storage, db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { Worksheet, WorksheetPhoto, WorksheetPhotoPhase } from '../../types/worksheet';
import { compressImage } from '../../lib/imageCompress';
import ImageModal from '../common/ImageModal';
import { CameraIcon, PhotoIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface Props {
  worksheet: Worksheet;
  readOnly?: boolean;
}

const PHASE: Record<WorksheetPhotoPhase, { label: string; cls: string }> = {
  before: { label: 'Előtte', cls: 'bg-amber-500/90 text-white' },
  after: { label: 'Utána', cls: 'bg-emerald-600/90 text-white' },
  other: { label: 'Fotó', cls: 'bg-gray-700/80 text-white' }
};

/** Fotódokumentáció a munkalaphoz: kamera/galéria, tömörítés, előtte/utána, törlés, nagyítás. */
const WorksheetPhotos: React.FC<Props> = ({ worksheet, readOnly = false }) => {
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<WorksheetPhotoPhase>('other');
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const photos = (worksheet.photos || []).slice().sort((a, b) => (a.phase === b.phase ? 0 : a.phase === 'before' ? -1 : b.phase === 'before' ? 1 : a.phase === 'after' ? -1 : 1));

  const pick = (p: WorksheetPhotoPhase) => { setPhase(p); inputRef.current?.click(); };

  const onFiles = async (files: FileList | null) => {
    if (!files || !files.length || !user) return;
    const list = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!list.length) { toast.error('Csak képfájl tölthető fel'); return; }
    try {
      setBusy(`0/${list.length}`);
      const added: WorksheetPhoto[] = [];
      for (let i = 0; i < list.length; i++) {
        setBusy(`${i + 1}/${list.length}`);
        const { blob, width, height } = await compressImage(list[i], 1600, 0.82);
        const id = `ph_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const path = `worksheets/${worksheet.organizationId || 'org'}/${worksheet.id}/${id}.jpg`;
        const r = storageRef(storage, path);
        await uploadBytes(r, blob, { contentType: 'image/jpeg', customMetadata: { worksheetId: worksheet.id, phase } });
        const url = await getDownloadURL(r);
        added.push({ id, url, path, phase, width, height, size: blob.size, takenAt: Timestamp.now(), uploadedBy: user.id, uploadedByName: user.displayName || user.email || '' });
      }
      await updateDoc(doc(db, 'worksheets', worksheet.id), { photos: arrayUnion(...added), updatedAt: Timestamp.now() });
      toast.success(added.length === 1 ? 'Fotó feltöltve' : `${added.length} fotó feltöltve`);
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'A feltöltés nem sikerült');
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (p: WorksheetPhoto) => {
    if (!window.confirm('Törlöd ezt a fotót?')) return;
    try {
      setBusy('törlés');
      try { await deleteObject(storageRef(storage, p.path)); } catch { /* már nincs meg */ }
      await updateDoc(doc(db, 'worksheets', worksheet.id), { photos: (worksheet.photos || []).filter(x => x.id !== p.id), updatedAt: Timestamp.now() });
    } catch (e: any) { toast.error(e?.message || 'A törlés nem sikerült'); } finally { setBusy(null); }
  };

  const setCaption = async (p: WorksheetPhoto, caption: string) => {
    try { await updateDoc(doc(db, 'worksheets', worksheet.id), { photos: (worksheet.photos || []).map(x => x.id === p.id ? { ...x, caption } : x), updatedAt: Timestamp.now() }); }
    catch { toast.error('A felirat mentése nem sikerült'); }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-3 py-2 sm:px-4 sm:py-3 flex items-center justify-between cursor-pointer bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-700 dark:to-purple-700" onClick={() => setExpanded(e => !e)}>
        <div className="flex items-center gap-2">
          <PhotoIcon className="w-4 h-4 text-white" />
          <h3 className="text-xs sm:text-sm font-semibold text-white">Fotók</h3>
          <span className="text-[10px] sm:text-xs text-white/80">({photos.length})</span>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => pick('before')} disabled={!!busy} className="inline-flex items-center h-7 px-2 rounded-md text-[11px] font-medium bg-white/15 text-white hover:bg-white/25 disabled:opacity-50"><CameraIcon className="w-3.5 h-3.5 mr-1" />Előtte</button>
            <button type="button" onClick={() => pick('after')} disabled={!!busy} className="inline-flex items-center h-7 px-2 rounded-md text-[11px] font-medium bg-white/15 text-white hover:bg-white/25 disabled:opacity-50"><CameraIcon className="w-3.5 h-3.5 mr-1" />Utána</button>
            <button type="button" onClick={() => pick('other')} disabled={!!busy} className="inline-flex items-center h-7 px-2 rounded-md text-[11px] font-medium bg-white text-primary-700 hover:bg-primary-50 disabled:opacity-50"><CameraIcon className="w-3.5 h-3.5 mr-1" />{busy ? busy : 'Fotó'}</button>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => onFiles(e.target.files)} />

      {expanded && (
        <div className="p-3">
          {photos.length === 0 ? (
            <div className="text-center py-6 text-sm text-gray-500 dark:text-gray-400">
              {readOnly ? 'Nincs fotó.' : 'Nincs még fotó. Készíts „Előtte” és „Utána” képeket a munkáról – a PDF-be és az ügyfélnek küldött munkalapra is rákerülnek.'}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map(p => (
                <div key={p.id} className="relative group rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-900 aspect-square">
                  <ImageModal src={p.url} alt={p.caption || PHASE[p.phase].label} thumbnailClassName="w-full h-full object-cover" />
                  <span className={`absolute top-1 left-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${PHASE[p.phase].cls}`}>{PHASE[p.phase].label}</span>
                  {!readOnly && (
                    <button type="button" onClick={() => remove(p)} className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 focus:opacity-100 sm:opacity-0 max-sm:opacity-100" aria-label="Törlés"><TrashIcon className="w-3.5 h-3.5" /></button>
                  )}
                  {!readOnly ? (
                    <input
                      defaultValue={p.caption || ''}
                      onBlur={e => { if ((e.target.value || '') !== (p.caption || '')) setCaption(p, e.target.value.trim()); }}
                      placeholder="Felirat…"
                      className="crm-input-raw absolute bottom-0 inset-x-0 h-6 px-1.5 text-[11px] bg-black/55 text-white placeholder-white/60 border-0 focus:outline-none"
                    />
                  ) : p.caption ? (
                    <div className="absolute bottom-0 inset-x-0 px-1.5 py-0.5 text-[11px] bg-black/55 text-white truncate">{p.caption}</div>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {busy && <div className="px-3 pb-2 text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1"><XMarkIcon className="w-3 h-3 opacity-0" />Feltöltés: {busy}</div>}
    </div>
  );
};

export default WorksheetPhotos;
